import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
} from '@crono-one/design-system';

import { DynamicsEnvironment } from 'hooks/services/dynamics/useDynamicsEnvironments';
import { cn } from 'utils/style-utils';

interface DynamicsEnvironmentPickerProps {
  environments: DynamicsEnvironment[];
  onCancel: () => void;
  onConnect: (environment: DynamicsEnvironment) => void;
}

/**
 * Shown between the two Microsoft sign-ins when the account can open two or
 * more environments. Names can repeat (two "Sales Trial" were observed), so the
 * URL always shows in full next to the name.
 */
const DynamicsEnvironmentPicker = ({
  environments,
  onCancel,
  onConnect,
}: DynamicsEnvironmentPickerProps) => {
  const [selectedId, setSelectedId] = useState<string>();
  const selected = environments.find(
    ({ organizationId }) => organizationId === selectedId,
  );

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent className="max-w-131 gap-6 p-12">
        <DialogHeader>
          <DialogTitle className="text-2xl leading-7.5 font-bold text-gray-900">
            Choose your Dynamics 365 environment
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex max-h-85 flex-col gap-2">
          <p className="text-sm font-semibold text-gray-900">
            {environments.length} environments found
          </p>
          <RadioGroup
            value={selectedId ?? ''}
            onValueChange={(value) => setSelectedId(value as string)}
            className="flex flex-col gap-2"
          >
            {environments.map(({ organizationId, friendlyName, url }) => (
              <label
                key={organizationId}
                className={cn(
                  'flex min-h-16 cursor-pointer items-center justify-between gap-4 rounded-lg border border-neutral-300 p-5 hover:bg-gray-100',
                  { 'bg-gray-100': organizationId === selectedId },
                )}
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-gray-900">
                    {friendlyName || url}
                  </span>
                  <span className="text-xs break-all text-gray-700">{url}</span>
                </span>
                <RadioGroupItem
                  value={organizationId ?? ''}
                  className="mr-1 shrink-0"
                />
              </label>
            ))}
          </RadioGroup>
        </DialogBody>

        <DialogFooter>
          <DialogClose
            render={
              <Button variant="outline" size="lg" className="min-w-33">
                Cancel
              </Button>
            }
          />
          <Button
            size="lg"
            className="min-w-33"
            disabled={!selected}
            onClick={() => selected && onConnect(selected)}
          >
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DynamicsEnvironmentPicker;
